import { z } from 'zod';

// ============================================
// REUSABLE VALIDATION PRIMITIVES
// ============================================

const phoneSchema = z
  .string()
  .min(1, 'Phone number is required')
  .regex(
    /^[+]?[(]?[0-9]{1,3}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/,
    'Enter a valid phone number'
  )
  .refine((val) => val.replace(/\D/g, '').length >= 10, 'Phone number must have at least 10 digits')
  .transform((val) => val.replace(/\D/g, '')); // Normalize to digits

const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Enter a valid email address')
  .transform((val) => val.toLowerCase().trim());

const nameSchema = z
  .string()
  .min(2, 'Name must be at least 2 characters')
  .max(100, 'Name must be less than 100 characters')
  .regex(/^[\p{L}\p{M}\s'-]+$/u, 'Name contains invalid characters')
  .transform((val) => val.trim());

// ============================================
// ENUMS (Single Source of Truth)
// ============================================

export const INDUSTRIES = [
  'hvac',
  'electrical',
  'plumbing',
  'landscaping',
  'lawn-care',
  'window-cleaning',
  'pressure-washing',
  'pool-service',
  'pest-control',
  'appliance-repair',
  'glaziers',
  'handyman',
  'roofing',
  'garage-door',
  'locksmith',
  'painting',
  'carpet-cleaning',
  'tree-service',
  'septic-drain',
  'irrigation',
  'junk-removal',
  'fencing',
  'general-contractor',
  'other',
] as const;

export type Industry = (typeof INDUSTRIES)[number];

export const INDUSTRY_LABELS: Record<Industry, string> = {
  hvac: 'HVAC',
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  landscaping: 'Landscaping',
  'lawn-care': 'Lawn Care',
  'window-cleaning': 'Window Cleaning',
  'pressure-washing': 'Pressure Washing',
  'pool-service': 'Pool Service',
  'pest-control': 'Pest Control',
  'appliance-repair': 'Appliance Repair',
  glaziers: 'Glaziers / Glass Repair',
  handyman: 'Handyman Services',
  roofing: 'Roofing',
  'garage-door': 'Garage Door Services',
  locksmith: 'Locksmith',
  painting: 'Painting',
  'carpet-cleaning': 'Carpet / Floor Cleaning',
  'tree-service': 'Tree Service',
  'septic-drain': 'Septic / Drain Services',
  irrigation: 'Irrigation',
  'junk-removal': 'Junk Removal',
  fencing: 'Fencing',
  'general-contractor': 'General Contractor',
  other: 'Other',
};

export const COMPANY_SIZES = ['1-5', '6-10', '11-25', '26-50', '51-100', '100+'] as const;
export type CompanySize = (typeof COMPANY_SIZES)[number];

export const INQUIRY_TYPES = ['sales', 'support', 'partnership', 'media'] as const;
export type InquiryType = (typeof INQUIRY_TYPES)[number];

export const INQUIRY_TYPE_LABELS: Record<InquiryType, string> = {
  sales: 'Sales Inquiry',
  support: 'Customer Support',
  partnership: 'Partnership Opportunity',
  media: 'Media & Press',
};

export const DEPARTMENTS = ['sales', 'support', 'billing', 'partnerships'] as const;
export type Department = (typeof DEPARTMENTS)[number];

export const DEPARTMENT_LABELS: Record<Department, string> = {
  sales: 'Sales',
  support: 'Technical Support',
  billing: 'Billing',
  partnerships: 'Partnerships',
};

// ============================================
// FORM SCHEMAS
// ============================================

// Demo Request (5 required fields - research shows each field drops conversion 4.1%)
export const demoFormSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: phoneSchema,
  company: z.string().min(1, 'Company name required').max(200).transform((val) => val.trim()),
  companySize: z.enum(COMPANY_SIZES, { required_error: 'Please select your company size' }),
  industry: z.enum(INDUSTRIES).optional(),
  currentSoftware: z.string().max(200).optional(),
  // Honeypot field (should always be empty)
  website: z.string().max(0, 'Invalid submission').optional(),
});

// Contact Form (simpler - 4 fields)
export const contactFormSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  inquiryType: z.enum(INQUIRY_TYPES, { required_error: 'Please select inquiry type' }),
  message: z.string().min(10, 'Message must be at least 10 characters').max(2000),
  // Honeypot
  website: z.string().max(0, 'Invalid submission').optional(),
});

// Enhanced Contact Form with department
export const enhancedContactFormSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: z.string().optional().transform((val) => val?.replace(/\D/g, '') || ''),
  department: z.enum(DEPARTMENTS, { required_error: 'Please select a department' }),
  subject: z.string().min(3, 'Subject must be at least 3 characters').max(200),
  message: z.string().min(10, 'Message must be at least 10 characters').max(2000),
  // Honeypot
  website: z.string().max(0, 'Invalid submission').optional(),
});

// Newsletter (1 field only)
export const newsletterSchema = z.object({
  email: emailSchema,
});

// ============================================
// INFERRED TYPES
// ============================================

export type DemoFormData = z.infer<typeof demoFormSchema>;
export type ContactFormData = z.infer<typeof contactFormSchema>;
export type EnhancedContactFormData = z.infer<typeof enhancedContactFormSchema>;
export type NewsletterFormData = z.infer<typeof newsletterSchema>;

// Input types (before transform) - useful for form defaults
export type DemoFormInput = z.input<typeof demoFormSchema>;
export type ContactFormInput = z.input<typeof contactFormSchema>;
export type EnhancedContactFormInput = z.input<typeof enhancedContactFormSchema>;
